import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api/client';
import {
  getCustomDashboardRevisionData,
  getCustomDashboardRevisionHtml,
  type CustomDashboardTab,
} from '@/lib/api/custom-dashboard';
import { cn } from '@/lib/utils';
import { CustomDashboardViewer } from './CustomDashboardViewer';

export interface CustomDashboardTabViewersProps {
  accountId: string;
  authHeaders: Record<string, string>;
  tabs: CustomDashboardTab[];
  activeTabId?: string;
  onNavigation: () => void;
}

interface SlotProps extends Omit<CustomDashboardTabViewersProps, 'tabs' | 'activeTabId'> {
  tabId: string;
  tabName: string;
  activeRevision: number;
  isActive: boolean;
}

function TabViewerSlot({
  accountId,
  tabId,
  tabName,
  activeRevision,
  authHeaders,
  isActive,
  onNavigation,
}: SlotProps) {
  const retry = (failureCount: number, error: Error) =>
    !(error instanceof ApiError && error.status === 404) && failureCount < 2;
  const htmlQuery = useQuery({
    queryKey: ['custom-dashboard-revision-html', accountId, tabId, activeRevision],
    queryFn: ({ signal }) =>
      getCustomDashboardRevisionHtml(accountId, tabId, activeRevision, authHeaders, signal),
    staleTime: Infinity,
    retry,
  });
  const dataQuery = useQuery({
    queryKey: ['custom-dashboard-revision-data', accountId, tabId, activeRevision],
    queryFn: ({ signal }) =>
      getCustomDashboardRevisionData(accountId, tabId, activeRevision, authHeaders, signal),
    staleTime: Infinity,
    retry,
  });
  const error = htmlQuery.error ?? dataQuery.error;
  const isFetching = htmlQuery.isFetching || dataQuery.isFetching;

  return (
    <div
      data-tab-id={tabId}
      className={cn('absolute inset-0 flex', !isActive && 'invisible')}
      inert={!isActive}
      aria-hidden={isActive ? undefined : true}
    >
      {htmlQuery.data !== undefined && dataQuery.data !== undefined ? (
        <CustomDashboardViewer
          html={htmlQuery.data}
          dataBuffer={dataQuery.data}
          onNavigation={onNavigation}
          isActive={isActive}
          sourceName={tabName}
        />
      ) : isFetching ? (
        <Skeleton aria-label="読み込み中" className="h-full w-full" />
      ) : error != null ? (
        <div className="flex h-full w-full items-center justify-center p-6">
          <Alert variant="destructive" className="max-w-xl">
            <AlertTitle>ダッシュボードを取得できませんでした</AlertTitle>
            <AlertDescription className="space-y-4">
              <p>{error instanceof Error ? error.message : String(error)}</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void Promise.all([htmlQuery.refetch(), dataQuery.refetch()])}
              >
                再試行
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      ) : null}
    </div>
  );
}

export function CustomDashboardTabViewers({
  accountId,
  authHeaders,
  tabs,
  activeTabId,
  onNavigation,
}: CustomDashboardTabViewersProps) {
  const [mountedIds, setMountedIds] = useState<string[]>([]);
  const previousAccountIdRef = useRef(accountId);

  useEffect(() => {
    const accountChanged = previousAccountIdRef.current !== accountId;
    previousAccountIdRef.current = accountId;
    const availableIds = tabs.map((tab) => tab.tabId);
    setMountedIds((current) => {
      const kept = (accountChanged ? [] : current).filter((id) => availableIds.includes(id));
      if (activeTabId && availableIds.includes(activeTabId) && !kept.includes(activeTabId))
        kept.push(activeTabId);
      return kept.length === current.length && kept.every((id, index) => id === current[index])
        ? current
        : kept;
    });
  }, [accountId, tabs, activeTabId]);

  const tabById = useMemo(() => new Map(tabs.map((tab) => [tab.tabId, tab])), [tabs]);
  return (
    <div className="relative min-h-0 flex-1">
      {mountedIds.map((tabId) => {
        const tab = tabById.get(tabId);
        if (!tab) return null;
        return (
          <TabViewerSlot
            key={`${accountId}:${tabId}:${tab.activeRevision}`}
            accountId={accountId}
            tabId={tabId}
            tabName={tab.name}
            activeRevision={tab.activeRevision}
            authHeaders={authHeaders}
            isActive={tabId === activeTabId}
            onNavigation={onNavigation}
          />
        );
      })}
    </div>
  );
}
